import { DynamicFrame, labOf } from './day-light';

/**
 * The dynamic wallpaper as one still picture (2026-10-12): every frame a diagonal band, from the
 * brightest at the top left corner to the darkest at the bottom right, each band the same area, the
 * cuts sharp.
 * The page's previews (the hero, the section swatches, the widget's preview, the size cards) are drawn
 * this way, so every picture on the page shows the whole day.
 */

/**
 * The frames in band order, brightest to darkest: by the sun's height (noon first, midnight last; Light
 * before Dark), the morning before the afternoon at the same height
 */
export function bandOrder<T extends DynamicFrame>( frames: T[] ): T[] {
  return [ ...frames ].sort( ( a, b ) => ( b.altitude - a.altitude ) || ( a.hour - b.hour ) );
}

/** how far apart two frames' palettes look: the mean OKLab distance between their colours */
function lookDistance( a: string[], b: string[] ): number {
  let sum = 0;
  for ( let i = 0; i < a.length; i++ ) {
    const x = labOf( a[ i ] ), y = labOf( b[ i ] );
    sum += Math.hypot( x[ 0 ] - y[ 0 ], x[ 1 ] - y[ 1 ], x[ 2 ] - y[ 2 ] );
  }
  return a.length ? sum / a.length : 0;
}

/**
 * The looks of the day, one frame for each (owner 2026-10-13: as many equal bands as the day has
 * different colours, not one per frame): the frames in band order are grouped while they look the same
 * (OKLab 0.03 apart, a little over what the eye tells apart), and each group gives one frame, the
 * brightest group its brightest (noon), the darkest group its darkest (midnight), any other its middle.
 */
export function distinctLooks<T extends DynamicFrame>( ordered: T[], threshold = 0.03 ): T[] {
  const groups: T[][] = [];
  for ( const f of ordered ) {
    const group = groups[ groups.length - 1 ];
    if ( group && lookDistance( group[ 0 ].colors, f.colors ) <= threshold ) group.push( f );
    else groups.push( [ f ] );
  }
  return groups.map( ( g, i ) => i === 0 ? g[ 0 ] : i === groups.length - 1 ? g[ g.length - 1 ] : g[ Math.floor( ( g.length - 1 ) / 2 ) ] );
}

/** the diagonal position s = x / w + y / h (0..2) with a share `f` of the area before it */
function cut( f: number ): number {
  return f <= 0.5 ? Math.sqrt( 2 * f ) : 2 - Math.sqrt( 2 * ( 1 - f ) );
}

/**
 * The bands as pixels: band i takes its pixels from `layers[ i ]` (RGBA, w × h each) wherever the
 * diagonal position x / w + y / h is past its cut. Done pixel by pixel rather than with canvas clips
 * and canvas-to-canvas draws, so every browser gives the same picture (Safari drew a canvas into
 * another later than asked, and the bands showed each other).
 */
export function composeBands( w: number, h: number, layers: Uint8ClampedArray[] ): Uint8ClampedArray {
  const n = layers.length;
  const out = new Uint8ClampedArray( w * h * 4 );
  if ( !n ) return out;
  const cuts: number[] = [];
  for ( let i = 1; i < n; i++ ) cuts.push( cut( i / n ) );
  for ( let y = 0; y < h; y++ ) {
    const sy = ( y + 0.5 ) / h;
    for ( let x = 0; x < w; x++ ) {
      const s = ( x + 0.5 ) / w + sy;
      let band = 0;
      while ( band < cuts.length && s >= cuts[ band ] ) band++;
      const p = ( y * w + x ) * 4, src = layers[ band ];
      out[ p ] = src[ p ];
      out[ p + 1 ] = src[ p + 1 ];
      out[ p + 2 ] = src[ p + 2 ];
      out[ p + 3 ] = 255;
    }
  }
  return out;
}
